import { Card, Chip, EmptyState, LoadingRows, StatCard } from '../family-finance/shared'
import type { FantasyState } from './useFantasy'
import { fmtPts, fmtSigned, POSITION_TONES } from './useFantasy'

export default function OverviewTab({
  state,
  onGoToTab,
}: {
  state: FantasyState
  onGoToTab: (tab: string) => void
}) {
  const { loading, roster, connections, outlook, ranked, recommendations } = state

  if (loading) return <LoadingRows rows={6} />

  const topAvailable = ranked.filter((r) => r.player.rosterStatus === 'available').slice(0, 5)

  // Concentration flags: same-NFL-team clusters and shared byes on the roster.
  const teamClusters = new Map<string, string[]>()
  const byeClusters = new Map<number, string[]>()
  for (const p of roster) {
    teamClusters.set(p.nflTeam, [...(teamClusters.get(p.nflTeam) ?? []), p.name])
    byeClusters.set(p.byeWeek, [...(byeClusters.get(p.byeWeek) ?? []), p.name])
  }
  const teamWarnings = [...teamClusters.entries()].filter(([, names]) => names.length >= 2)
  const byeWarnings = [...byeClusters.entries()].filter(([, names]) => names.length >= 3)

  return (
    <div className="space-y-6">
      {/* Stat row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Expected Wins"
          value={outlook ? `${fmtPts(outlook.expectedWins)} / ${outlook.totalWeeks}` : '—'}
          sub="Sum of weekly win probabilities (F10–F11)"
        />
        <StatCard
          label="Roster"
          value={`${roster.length} players`}
          sub={`${connections.length} league${connections.length === 1 ? '' : 's'} connected`}
        />
        <StatCard
          label="Best Available"
          value={topAvailable[0]?.player.name ?? '—'}
          sub={topAvailable[0] ? `Risk-adj. value ${fmtPts(topAvailable[0].analytics.scarcityWeightedValue)}` : undefined}
          subTone="green"
        />
        <StatCard
          label="Top Move"
          value={recommendations[0] ? fmtSigned(recommendations[0].deltaExpectedWins, 2) + ' wins' : 'None'}
          sub={recommendations[0] ? `Add ${recommendations[0].playerName}` : 'Roster is locally optimal'}
          subTone={recommendations[0] ? 'green' : 'slate'}
        />
      </div>

      {/* Correlated-risk warnings */}
      {(teamWarnings.length > 0 || byeWarnings.length > 0) && (
        <Card title="Correlated risk on your roster" subtitle="Shared idiosyncratic exposure the analytics are pricing in (F6, F7)">
          <div className="space-y-2">
            {teamWarnings.map(([team, names]) => (
              <div key={team} className="flex items-start gap-2 text-sm">
                <Chip tone="amber">{team} ×{names.length}</Chip>
                <span className="text-slate-600 dark:text-slate-300">
                  {names.join(', ')} share one offense — game script, injuries, and weather hit them together.
                </span>
              </div>
            ))}
            {byeWarnings.map(([week, names]) => (
              <div key={week} className="flex items-start gap-2 text-sm">
                <Chip tone="red">Week {week} bye ×{names.length}</Chip>
                <span className="text-slate-600 dark:text-slate-300">
                  {names.join(', ')} all sit the same week — that week projects as a near-certain loss.
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Weekly outlook */}
        <Card
          title="Season outlook"
          subtitle="Weekly win probability vs a league-typical opponent (F10)"
        >
          {!outlook ? (
            <EmptyState icon="📅" message="Connect a league to model your season." />
          ) : (
            <div className="space-y-1.5">
              {outlook.weeks.map((w) => (
                <div key={w.week} className="flex items-center gap-3 text-sm">
                  <span className="text-slate-500 w-10 shrink-0">W{w.week}</span>
                  <div className="flex-1 h-2 bg-slate-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full ${
                        w.winProbability >= 0.55
                          ? 'bg-emerald-500'
                          : w.winProbability >= 0.45
                            ? 'bg-amber-500'
                            : 'bg-red-500'
                      }`}
                      style={{ width: `${w.winProbability * 100}%` }}
                    />
                  </div>
                  <span className="text-slate-600 dark:text-slate-300 w-12 text-right shrink-0">
                    {(w.winProbability * 100).toFixed(0)}%
                  </span>
                  {w.byeOuts.length > 0 && (
                    <Chip tone="red">{w.byeOuts.length} on bye</Chip>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Top available */}
        <Card
          title="Top available players"
          subtitle="Ranked by scarcity-weighted, risk-adjusted value (F8–F9)"
          action={
            <button
              onClick={() => onGoToTab('players')}
              className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 text-sm"
            >
              View all →
            </button>
          }
        >
          {topAvailable.length === 0 ? (
            <EmptyState icon="🔍" message="No available players in the pool." />
          ) : (
            <div className="space-y-2">
              {topAvailable.map((entry) => (
                <div
                  key={entry.player.id}
                  className="flex items-center justify-between bg-slate-100/60 dark:bg-slate-900/60 rounded-lg px-3 py-2"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="text-slate-500 text-sm w-6">#{entry.rank}</span>
                    <div className="min-w-0">
                      <p className="text-slate-900 dark:text-white text-sm font-medium truncate">{entry.player.name}</p>
                      <p className="text-slate-500 text-xs">
                        {entry.player.nflTeam} · Bye {entry.player.byeWeek}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Chip tone={POSITION_TONES[entry.player.position]}>{entry.player.position}</Chip>
                    <span className="text-emerald-600 dark:text-emerald-400 text-sm font-medium w-14 text-right">
                      {fmtPts(entry.analytics.scarcityWeightedValue)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* My roster */}
      <Card title="My roster" subtitle="Fully adjusted projections and risk contributions">
        {roster.length === 0 ? (
          <EmptyState icon="🏈" message="No roster synced yet — connect a league." />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-slate-500 text-xs uppercase">
                  <th className="text-left font-medium pb-2 pr-4">Player</th>
                  <th className="text-left font-medium pb-2 pr-4">Pos</th>
                  <th className="text-right font-medium pb-2 pr-4">Bye</th>
                  <th className="text-right font-medium pb-2 pr-4">Adj. Proj</th>
                  <th className="text-right font-medium pb-2 pr-4">σ (season)</th>
                  <th className="text-right font-medium pb-2">VORP</th>
                </tr>
              </thead>
              <tbody>
                {ranked
                  .filter((r) => r.player.rosterStatus === 'mine')
                  .sort((a, b) => b.analytics.adjustedPoints - a.analytics.adjustedPoints)
                  .map((entry) => (
                    <tr key={entry.player.id} className="border-t border-slate-200/60 dark:border-slate-700/60">
                      <td className="py-2 pr-4 text-slate-900 dark:text-white">
                        {entry.player.name}
                        <span className="text-slate-500 ml-2 text-xs">{entry.player.nflTeam}</span>
                      </td>
                      <td className="py-2 pr-4">
                        <Chip tone={POSITION_TONES[entry.player.position]}>{entry.player.position}</Chip>
                      </td>
                      <td className="py-2 pr-4 text-right text-slate-500 dark:text-slate-400">{entry.player.byeWeek}</td>
                      <td className="py-2 pr-4 text-right text-slate-800 dark:text-slate-200">{fmtPts(entry.analytics.adjustedPoints, 0)}</td>
                      <td className="py-2 pr-4 text-right text-slate-500 dark:text-slate-400">±{fmtPts(entry.analytics.projectionStdDev, 0)}</td>
                      <td className="py-2 text-right text-emerald-600 dark:text-emerald-400">{fmtPts(entry.analytics.vorp, 0)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
